import React, { useEffect, useState } from 'react'
import { Box, Button, TextField, Select, MenuItem } from '@mui/material'
import PlayerService, { PlayerData } from '@/service/PlayerService'
import MemberService, { MemberData } from '@/service/MemberService'
import { GameItem } from '@/service/GameService'
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

interface GamePlayersProps {
    game: GameItem
}

const POSITIONS = ['CF', 'WF', 'MF', 'CB', 'SB', 'GK']

const GamePlayersTab = ({ game }: GamePlayersProps) => {
    const isSelfGame = game.type === '자체전'

    const [members, setMembers] = useState<MemberData[]>([])
    const [players, setPlayers] = useState<PlayerData[]>([])

    /** 선택된 team player */
    const [selectedPlayer, setSelectedPlayer] = useState<PlayerData | null>(null)

    /** 하단 입력폼 */
    const [mercenaryName, setMercenaryName] = useState('')
    const [mercenaryPosition1, setMercenaryPosition1] = useState('MF')
    const [mercenaryPosition2, setMercenaryPosition2] = useState('SB')

    useEffect(() => {
        if (!game.id) return
        MemberService.getMemberList().then(setMembers)
        PlayerService.getPlayersByGame(game.id).then(setPlayers)
    }, [game.id])

    /** ===== member → team 등록 로직 ===== */
    const findPlayer = (memberId: number) =>
        players.find(p => p.memberId === memberId)

    const getNextTeam = (player?: PlayerData) => {
        if (!player) return 'A'
        if (player.teamABType === 'A') return isSelfGame ? 'B' : null
        return null
    }

    const handleMemberClick = async (member: MemberData) => {
        const player = findPlayer(member.id)
        const nextTeam = getNextTeam(player)

        if (player && !nextTeam) {
            await PlayerService.deletePlayer(player.id)
            setPlayers(prev => prev.filter(p => p.id !== player.id))
            return
        }

        if (nextTeam) {
            const saved = await PlayerService.postPlayer({
                id: player?.id,
                gameId: game.id!,
                memberId: member.id,
                name: member.name,
                position1: member.position1,
                position2: member.position2,
                teamABType: nextTeam
            })

            setPlayers(prev =>
                player
                    ? prev.map(p => (p.id === saved.id ? saved : p))
                    : [...prev, saved]
            )
        }
    }

    /** ===== team 클릭 → 하단 폼 채우기 ===== */
    const handleTeamPlayerClick = (player: PlayerData) => {
        // 이미 선택된 플레이어를 다시 클릭한 경우 → 팀 이동
        if (selectedPlayer?.id === player.id) {
            togglePlayerTeam(player)
            return
        }

        // 처음 클릭 → 선택
        setSelectedPlayer(player)

        // 하단 입력폼 채우기
        setMercenaryName(player.name)
        setMercenaryPosition1(player.position1)
        setMercenaryPosition2(player.position2 ?? '')
    }


    /** ===== team A/B 토글 ===== */
    const togglePlayerTeam = async (player: PlayerData) => {
        if (!isSelfGame) return

        const saved = await PlayerService.postPlayer({
            ...player,
            teamABType: player.teamABType === 'A' ? 'B' : 'A'
        })

        setPlayers(prev =>
            prev.map(p => (p.id === saved.id ? saved : p))
        )
    }

    /** ===== 하단 버튼 모드 ===== */
    const isDirty =
        selectedPlayer &&
        (
            mercenaryName !== selectedPlayer.name ||
            mercenaryPosition1 !== selectedPlayer.position1 ||
            mercenaryPosition2 !== selectedPlayer.position2
        )

    const isDeleteMode = selectedPlayer && !isDirty

    const resetForm = () => {
        setSelectedPlayer(null)
        setMercenaryName('')
        setMercenaryPosition1('MF')
        setMercenaryPosition2('SB')
    }

    const handleMercenaryAction = async () => {
        if (!mercenaryName.trim()) return

        /** 🔴 삭제 */
        if (isDeleteMode && selectedPlayer) {
            await PlayerService.deletePlayer(selectedPlayer.id)
            setPlayers(prev => prev.filter(p => p.id !== selectedPlayer.id))
            resetForm()
            return
        }

        /** 🟢 등록 or 수정 */
        const saved = await PlayerService.postPlayer({
            id: selectedPlayer?.id,
            gameId: game.id!,
            name: mercenaryName,
            position1: mercenaryPosition1,
            position2: mercenaryPosition2,
            teamABType: 'A'
        })

        setPlayers(prev =>
            selectedPlayer
                ? prev.map(p => (p.id === saved.id ? saved : p))
                : [...prev, saved]
        )

        resetForm()
    }

    /** ===== position 정렬 ===== */
    const POSITION_ORDER = ['CF', 'WF', 'MF', 'CB', 'SB', 'GK']
    const rank = (p?: string) =>
        POSITION_ORDER.indexOf(p ?? '') === -1 ? 999 : POSITION_ORDER.indexOf(p!)

    const sortByPosition = (list: PlayerData[]) =>
        [...list].sort((a, b) => rank(a.position1) - rank(b.position1))

    const teamA = sortByPosition(players.filter(p => p.teamABType === 'A'))
    const teamB = sortByPosition(players.filter(p => p.teamABType === 'B'))

    const [copyOpen, setCopyOpen] = useState(false);
    const [copyText, setCopyText] = useState('');

    const handleCopyToClipboard = async () => {
        const formatPlayer = (p: PlayerData) =>
            `${p.name}${p.position1}`;

        const gameInfo =
`${game.matchDt ?? ''} ${game.time ?? ''} ${game.vsName ?? '자체전'}
${game.place ?? ''} (${teamA.length + teamB.length}명)`;

        const teamAInfo =
`
--Team A (${teamA.length}) --
${teamA.map(formatPlayer).join(' ')}`;

        const teamBInfo = isSelfGame
            ?
`
--Team B (${teamB.length}) --
${teamB.map(formatPlayer).join(' ')}` : '';

        const resultText =
`${gameInfo}
${teamAInfo}
${teamBInfo}

* 경기중 과도한지시 주의 (휴식시간에 차분하게 전달)
* 타인에 대한 존중 (판정에항의, 욕설/비난/조롱 하지 않기)
* 거친플레이(사람을 향한 태클, 점프할때 미는 행위 등) 주의`;

        setCopyText(resultText);
        setCopyOpen(true);

    };

    return (
        <Box className="game-players">
            <Box className="player-layout">

                {/* MEMBER */}
                <Box className="member-list">
                    <h4 onClick={handleCopyToClipboard}>Member <span>({teamA.length+teamB.length})</span>
                    </h4>

                    <Box className="member-scroll">
                        {members.map(member => {
                            const player = findPlayer(member.id)

                            return (
                                <Box
                                    key={member.id}
                                    className="member-row"
                                    onClick={() => handleMemberClick(member)}
                                >
                                    <div className="member-line">
                                        <span className="member-info">{member.name}</span>
                                        <span className="position">
                                            {member.position1}
                                            {member.position2 && `/${member.position2}`}
                                        </span>
                                        {player && (
                                            <span className={`team-badge team-${player.teamABType}`} />
                                        )}
                                    </div>
                                </Box>
                            )
                        })}
                    </Box>
                </Box>

                {/* TEAM A */}
                <Box className="team-box">
                    <h4>Team A <span>({teamA.length})</span></h4>
                    <div className="team-scroll">
                        {teamA.map(p => (
                            <div
                                key={p.id}
                                className={`team-player clickable ${selectedPlayer?.id === p.id ? 'selected' : ''}`}
                                onClick={() => handleTeamPlayerClick(p)}
                                onDoubleClick={() => togglePlayerTeam(p)}
                            >
                                    <span className={`member-info ${!p.memberId ? 'mercenary-position' : ''}`}>
                                        {p.name}
                                    </span>

                                    <span className={`position`}>
                                        {p.position1}
                                        {p.position2 && `/${p.position2}`}
                                    </span>
                            </div>
                        ))}
                    </div>
                </Box>

                {/* TEAM B */}
                {isSelfGame && (
                    <Box className="team-box">
                        <h4>Team B <span>({teamB.length})</span></h4>
                        <div className="team-scroll">
                            {teamB.map(p => (
                                <div
                                    key={p.id}
                                    className={`team-player clickable ${selectedPlayer?.id === p.id ? 'selected' : ''}`}
                                    onClick={() => handleTeamPlayerClick(p)}
                                    onDoubleClick={() => togglePlayerTeam(p)}
                                >
                                    <span className={`member-info ${!p.memberId ? 'mercenary-position' : ''}`}>
                                        {p.name}
                                    </span>

                                    <span className={`position`}>
                                        {p.position1}
                                        {p.position2 && `/${p.position2}`}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Box>
                )}
            </Box>

            {/* 하단 입력폼 */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                    size="small"
                    placeholder="이름"
                    value={mercenaryName}
                    onChange={e => setMercenaryName(e.target.value)}
                />

                <Select sx={{ height: '40px' }}
                    size="small"
                    value={mercenaryPosition1}
                    onChange={e => setMercenaryPosition1(e.target.value)}
                >
                    {POSITIONS.map(p => (
                        <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                </Select>

                <Select sx={{ height: '40px' }}
                    size="small"
                    value={mercenaryPosition2}
                    onChange={e => setMercenaryPosition2(e.target.value)}
                >
                    {POSITIONS.map(p => (
                        <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                </Select>

                <Button sx={{ height: '40px' }}
                    size="small"
                    variant="contained"
                    color={isDeleteMode ? 'error' : 'primary'}
                    onClick={handleMercenaryAction}
                >
                    {isDeleteMode ? '삭제' : '등록'}
                </Button>
            </Box>

            <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>📋 게임 정보 복사</DialogTitle>

                <DialogContent>
                    <textarea
                        value={copyText}
                        readOnly
                        autoFocus
                        style={{
                            width: '100%',
                            height: '300px',
                            fontSize: '14px'
                        }}
                        onFocus={(e) => e.target.select()}
                    />
                    <div style={{ marginTop: 10, fontSize: 13 }}>
                        👉 <b>Ctrl + C</b> 를 눌러 복사하세요.
                    </div>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setCopyOpen(false)}>닫기</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default GamePlayersTab
